import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

interface Branch {
  id: number;
  name: string;
  code: string;
  isHeadOffice: boolean;
  isActive: boolean;
  userPermissions?: {
    branchRole: string;
    isPrimaryBranch: boolean;
    canManageBranch: boolean;
    canManageUsers: boolean;
    canExportData: boolean;
  };
}

interface BranchContextType {
  branches: Branch[];
  currentBranch: Branch | null;
  primaryBranch: Branch | null;
  loading: boolean;
  error: string | null;
  switchBranch: (branchId: number | 'all') => void;
  refreshBranches: () => Promise<void>;
  canViewAllBranches: boolean;
  isViewingAllBranches: boolean;
}

const BranchContext = createContext<BranchContextType | undefined>(undefined);

export const useBranch = () => {
  const context = useContext(BranchContext);
  if (!context) {
    throw new Error('useBranch must be used within a BranchProvider');
  }
  return context;
};

interface BranchProviderProps {
  children: ReactNode;
}

export const BranchProvider: React.FC<BranchProviderProps> = ({ children }) => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [currentBranch, setCurrentBranch] = useState<Branch | null>(null);
  const [primaryBranch, setPrimaryBranch] = useState<Branch | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isViewingAllBranches, setIsViewingAllBranches] = useState(false);

  // Check if user can view all branches (admin or has permission)
  const canViewAllBranches = user?.role === 'admin' || 
    user?.branches?.some((b: any) => b.can_view_all_branches) || false;

  // Fetch user's branches
  const fetchBranches = async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/branches/my-branches');
      const { branches: userBranches, primaryBranch: primary } = response.data;
      
      setBranches(userBranches || []);
      setPrimaryBranch(primary);
      
      // Set initial current branch
      if (!currentBranch && userBranches.length > 0) {
        // Check if there's a saved branch preference first
        const savedBranchId = localStorage.getItem('currentBranchId');
        let branchToSet = null;

        if (savedBranchId) {
          // Try to restore saved branch
          if (savedBranchId === 'all' && canViewAllBranches) {
            setCurrentBranch(null);
            setIsViewingAllBranches(true);
            api.defaults.headers.common['X-Branch-Id'] = 'all';
            return; // Exit early for "all branches" mode
          } else {
            branchToSet = userBranches.find((b: any) => b.id === parseInt(savedBranchId));
          }
        }

        // If no saved branch or saved branch not found, use default
        if (!branchToSet) {
          branchToSet = primary || userBranches[0];
          // Save the default choice to localStorage
          localStorage.setItem('currentBranchId', branchToSet.id.toString());
        }

        setCurrentBranch(branchToSet);
        // Set API header for branch context
        api.defaults.headers.common['X-Branch-Id'] = branchToSet.id;
      }
    } catch (err: any) {
      console.error('Error fetching branches:', err);
      setError(err.response?.data?.error || 'Failed to fetch branches');
    } finally {
      setLoading(false);
    }
  };

  // Switch to a different branch
  const switchBranch = (branchId: number | 'all') => {
    // Check if we're actually switching to a different branch
    const isChangingBranch = (branchId === 'all' && !isViewingAllBranches) || 
                            (typeof branchId === 'number' && currentBranch?.id !== branchId);
    
    if (branchId === 'all' && canViewAllBranches) {
      // View all branches (admin only)
      setCurrentBranch(null);
      setIsViewingAllBranches(true);
      api.defaults.headers.common['X-Branch-Id'] = 'all';
      localStorage.setItem('currentBranchId', 'all');
      
      // Refresh the page to reload all data for all branches
      if (isChangingBranch) {
        window.location.reload();
      }
    } else if (typeof branchId === 'number') {
      // Switch to specific branch
      const branch = branches.find(b => b.id === branchId);
      if (branch) {
        setCurrentBranch(branch);
        setIsViewingAllBranches(false);
        api.defaults.headers.common['X-Branch-Id'] = branchId;
        
        // Save preference to localStorage
        localStorage.setItem('currentBranchId', branchId.toString());
        
        // Refresh the page to reload all data for the new branch
        if (isChangingBranch) {
          window.location.reload();
        }
      }
    }
  };

  // Initialize on mount and when auth changes
  useEffect(() => {
    if (isAuthenticated) {
      fetchBranches();
    } else if (!authLoading) {
      // Only clear branch data when auth loading is complete and user is definitely not authenticated
      // This prevents clearing data during the initial auth check on page refresh
      setBranches([]);
      setCurrentBranch(null);
      setPrimaryBranch(null);
      setIsViewingAllBranches(false);
      delete api.defaults.headers.common['X-Branch-Id'];
      // Only clear localStorage when user is definitely logged out (not during refresh)
      localStorage.removeItem('currentBranchId');
    }
  }, [isAuthenticated, authLoading]);

  // Note: Branch restoration is now handled directly in fetchBranches function

  const value: BranchContextType = {
    branches,
    currentBranch,
    primaryBranch,
    loading,
    error,
    switchBranch,
    refreshBranches: fetchBranches,
    canViewAllBranches,
    isViewingAllBranches
  };

  return <BranchContext.Provider value={value}>{children}</BranchContext.Provider>;
};

export default BranchContext;
